import React, { useState, useEffect } from "react";
import { FaMagnet } from "react-icons/fa";
import { FiCalendar, FiEye } from "react-icons/fi";
import { FiShoppingBag } from "react-icons/fi";
import { FaBucket } from "react-icons/fa6";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Paginator } from "primereact/paginator";
import { Dropdown, DropdownButton } from "react-bootstrap";
import InfoHeader from "../components/InfoHeader";
import { Calendar } from "primereact/calendar";
import "../styles/rtc.css"
import { fetchRTCSummary, fetchRTCHistory, fetchCleaningActivityDataTurmeric, saveActualCount } from "../components/API/Api";
import UnsafeEventsModal from "../components/UnsafeEventsModal";
import { SiTraefikmesh } from "react-icons/si";
import { FaHistory } from "react-icons/fa";
import CleaningHistoryModal from "../components/MagneticGrillCleanModal";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const RowSaveButton = ({ rowKey, saveCallbacksRef, onSave, hasExistingValue }) => {
  const [enabled, setEnabled] = useState(!!hasExistingValue);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isUpdate, setIsUpdate] = useState(!!hasExistingValue);

  useEffect(() => {
    saveCallbacksRef.current[rowKey] = (hasValue) => setEnabled(hasValue);
    return () => { delete saveCallbacksRef.current[rowKey]; };
  }, [rowKey, saveCallbacksRef]);

  const handleClick = async () => {
    setSaving(true);
    const success = await onSave();
    setSaving(false);
    if (success) {
      setSaved(true);
      setIsUpdate(true);
      setTimeout(() => setSaved(false), 2000);
    }
  };

  return (
    <button
      disabled={!enabled || saving}
      onClick={handleClick}
      style={{
        padding: "4px 10px",
        fontSize: "12px",
        border: "none",
        borderRadius: "4px",
        cursor: enabled && !saving ? "pointer" : "not-allowed",
        backgroundColor: saved ? "#28a745" : enabled ? "#0d6efd" : "#ccc",
        color: "#fff",
        minWidth: "55px",
      }}
    >
      {saving ? "..." : saved ? "\u2713" : isUpdate ? "Update" : "Save"}
    </button>
  );
};

const RTC = () => {
  const [selectedComponent, setSelectedComponent] = useState("Premixer 1");
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [shiftFilter, setShiftFilter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    rowsPerPage: 5,
  });
  const [cardData, setCardData] = useState({
    primix1: {
      last_magnetic_plate_cleaning_time: null,
      safety_mesh_lifted_status: null,
      unsafe_event: []
    },
    primix2: {
      last_magnetic_plate_cleaning_time: null,
      safety_mesh_lifted_status: null,
      unsafe_event: []
    },
    finalmixer1: {
      last_magnetic_plate_cleaning_time: null,
      safety_mesh_lifted_status: null,
      unsafe_event: []
    },
    finalmixer2: {
      last_magnetic_plate_cleaning_time: null,
      safety_mesh_lifted_status: null,
      unsafe_event: []
    },
    grinder1: {
      sack_count: 0,
      bucket_count: 0
    },
    grinder2: {
      sack_count: 0,
      bucket_count: 0
    }
  });
  const [showUnsafeModal, setShowUnsafeModal] = useState(false);
  const [currentComponent, setCurrentComponent] = useState("primix1");
  const [, setCurrentRtcComponent] = useState("primix1");
  const [unsafeEvents, setUnsafeEvents] = useState({
    primix1: [],
    primix2: [],
    finalmixer1: [],
    finalmixer2: []
  });

  const [cleaningHistoryData, setCleaningHistoryData] = useState({
    primix1: [],
    primix2: [],
    finalmixer1: [],
    finalmixer2: []
  });

  const [rtcUnsafeEvents] = useState({
    primix1: [],
    primix2: [],
    finalmixer1: [],
    finalmixer2: []
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [showCleaningHistoryModal, setShowCleaningHistoryModal] = useState(false);
  const [currentCleaningComponent, setCurrentCleaningComponent] = useState(null);
  const [, setCurrentCleaningType] = useState(null);
  const actualCountsRef = React.useRef({});
  const saveCallbacksRef = React.useRef({});

  const handleActualCountChange = (key, field, value) => {
    if (!actualCountsRef.current[key]) actualCountsRef.current[key] = {};
    actualCountsRef.current[key][field] = value;
    const row = actualCountsRef.current[key];
    const hasValue = !!(row.actual_sack_count || row.actual_bucket_count);
    if (saveCallbacksRef.current[key]) saveCallbacksRef.current[key](hasValue);
  };

  const handleSaveActualCount = async (rowData) => {
    const key = rowData.timestamp_range;
    const counts = actualCountsRef.current[key] || {};
    try {
      const payload = {
        section: `rtc#${getEquipmentParam()}#hourly#count`,
        date: extractDateFromTimestampRange(rowData.timestamp_range),
        time: extractTimeFromTimestampRange(rowData.timestamp_range),
        shift: rowData.shift,
        actual_sack_count: Number(counts.actual_sack_count) || 0,
        actual_bucket_count: Number(counts.actual_bucket_count) || 0,
      };
      const response = await saveActualCount(payload);
      if (response.success) {
        setData(prev => prev.map(item =>
          item.timestamp_range === key
            ? {
                ...item,
                actual_sack_count: Number(counts.actual_sack_count) || 0,
                actual_bucket_count: Number(counts.actual_bucket_count) || 0,
              }
            : item
        ));
        return true;
      } else {
        alert(response.error || "Failed to save actual count");
        return false;
      }
    } catch (err) {
      alert("Error saving actual count: " + err.message);
      return false;
    }
  };

  // Determine view mode based on selected component
  const getViewMode = () => {
    if (selectedComponent.includes("Grinder")) {
      return "grinder";
    } else {
      return "mixer";
    }
  };

  // Get API equipment parameter based on selected component
  const getEquipmentParam = () => {
    const componentMap = {
      "Premixer 1": "primixer1",
      "Premixer 2": "primixer2",
      "Grinder 1": "grinder1",
      "Grinder 2": "grinder2",
      "Final Mixture 1": "finalmixer1",
      "Final Mixture 2": "finalmixer2",
    };

    return componentMap[selectedComponent] || "primixer1";
  };

  const viewMode = getViewMode();

  // Format date for API request
  const formatDateForAPI = (date) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  // Extract date from timestamp range (for UI display)
  const extractDateFromTimestampRange = (timestampRange) => {
    if (!timestampRange) return "";

    // Extract the date part from "01-05-25 07:00 AM to 01-05-25 08:00 AM"
    const match = timestampRange.match(/(\d{2}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  };

  // Extract time from timestamp range (for UI display)
  const extractTimeFromTimestampRange = (timestampRange) => {
    if (!timestampRange) return "";

    // Extract time parts from "01-05-25 07:00 AM to 01-05-25 08:00 AM"
    const match = timestampRange.match(/(\d{2}:\d{2} [AP]M) to .* (\d{2}:\d{2} [AP]M)/);
    return match ? `${match[1]} - ${match[2]}` : "";
  };

  const fetchCardData = async (startDate = null, endDate = null) => {
    setLoading(true);

    try {
      // Use imported API function
      const response = await fetchRTCSummary(startDate, endDate);
      // Alternative way using default export:
      // const response = await rtcApi.fetchSummary(startDate, endDate);
      if (response && response.body && response.body.success) {
        const apiData = response.body.success;
        const normalizedData = {
          primix1: {
            ...apiData.primix1,
            unsafe_event: apiData.primix1?.unsafe_event || []
          },
          primix2: {
            ...apiData.primix2,
            unsafe_event: apiData.primix2?.unsafe_event || []
          },
          finalmixer1: {
            ...apiData.final_mixture1,
            unsafe_event: apiData.final_mixture1?.unsafe_event || []
          },
          finalmixer2: {
            ...apiData.final_mixture2,
            unsafe_event: apiData.final_mixture2?.unsafe_event || []
          },
          grinder1: apiData.grinder1 || { sack_count: 0, bucket_count: 0 },
          grinder2: apiData.grinder2 || { sack_count: 0, bucket_count: 0 }
        };

        // Update cardData with normalized data
        setCardData(normalizedData);

        setCleaningHistoryData({
          primix1: apiData.primix1?.cleaning_history || [],
          primix2: apiData.primix2?.cleaning_history || [],
          finalmixer1: apiData.final_mixture1?.cleaning_history || [],
          finalmixer2: apiData.final_mixture2?.cleaning_history || []
        });

        // Extract unsafe events for each component
        setUnsafeEvents({
          primix1: (normalizedData.primix1?.unsafe_event || []).reverse(),
          primix2: (normalizedData.primix2?.unsafe_event || []).reverse(),
          finalmixer1: ((normalizedData.finalmixer1?.unsafe_event) || []).reverse(),
          finalmixer2: ((normalizedData.finalmixer2?.unsafe_event) || []).reverse()
        });
      } else {
        setError("Invalid response format from API");
      }
    } catch (err) {
      console.error("Error fetching card data:", err);
      setError("Error fetching card data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Refactored fetchTableData function
  const fetchTableData = async (
    page = 1,
    rowsPerPage = pagination.rowsPerPage,
    startDate = null,
    endDate = null,
    equipment = getEquipmentParam()
  ) => {
    setLoading(true);

    try {
      // Use imported API function
      const response = await fetchRTCHistory({
        page,
        rowsPerPage,
        startDate,
        endDate,
        equipment
      });

      // Alternative way using default export:
      // const response = await rtcApi.fetchHistory({
      //   page,
      //   rowsPerPage,
      //   startDate,
      //   endDate,
      //   equipment
      // });

      if (response && response.body && response.body.success) {
        const apiData = response.body.success;

        // Pre-populate actualCountsRef with existing actual counts
        actualCountsRef.current = {};
        (apiData.data || []).forEach(item => {
          const key = item.timestamp_range;
          const hasSack = item.actual_sack_count != null && item.actual_sack_count !== "";
          const hasBucket = item.actual_bucket_count != null && item.actual_bucket_count !== "";
          if (hasSack || hasBucket) {
            actualCountsRef.current[key] = {};
            if (hasSack) actualCountsRef.current[key].actual_sack_count = String(item.actual_sack_count);
            if (hasBucket) actualCountsRef.current[key].actual_bucket_count = String(item.actual_bucket_count);
          }
        });

        // Update data and pagination
        setData(apiData.data || []);
        setPagination({
          currentPage: apiData.page_no,
          totalPages: apiData.total_pages,
          totalRecords: apiData.total_records,
          rowsPerPage: apiData.no_of_records
        });
      } else {
        setError("Invalid response format from API");
        setData([]);
      }
    } catch (err) {
      console.error("Error fetching table data:", err);
      setError("Error fetching table data: " + err.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Effect to load data when filters or selected component changes
  useEffect(() => {
    // Set default date range to today if not set
    if (!dateRange[0] || !dateRange[1]) {
      const today = new Date();
      setDateRange([today, today]);
      fetchCardData(today, today);
      fetchTableData(1, pagination.rowsPerPage, today, today, getEquipmentParam());
    } else {
      fetchCardData(dateRange[0], dateRange[1]);
      fetchTableData(1, pagination.rowsPerPage, dateRange[0], dateRange[1], getEquipmentParam());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComponent]);

  // Effect to load data when component mounts (today's data)
  useEffect(() => {
    const today = new Date();
    setDateRange([today, today]);
    fetchCardData(today, today);
    fetchTableData(1, pagination.rowsPerPage, today, today, getEquipmentParam());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableContainerStyle = {
    width: "100%",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    padding: "15px",
    display: "flex",
    flexDirection: "column",
    minHeight: "400px",
    marginBottom: "20px",
  };

  const tableHeadingStyle = {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "18px",
    marginBottom: "15px",
    color: "#007BFF",
    justifyContent: "end",
    alignItems: "center",
  };

  const filterContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  };

  const dateFilterContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "start",
    alignItems: "center",
    padding: "0 15px",
    borderRadius: "8px",
    marginBottom: "20px",
    gap: "15px",
  };

  const applyDateFilter = () => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      fetchCardData(dateRange[0], dateRange[1]);
      fetchTableData(1, pagination.rowsPerPage, dateRange[0], dateRange[1], getEquipmentParam());
      // Reset shift filter when applying date filter
      setShiftFilter(null);
    }
  };

  const clearFilters = () => {
    const today = new Date();
    setDateRange([today, today]);
    setShiftFilter(null);
    fetchCardData(today, today);
    fetchTableData(1, pagination.rowsPerPage, today, today, getEquipmentParam());
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);

      const startDate = dateRange[0] ? formatDateForAPI(dateRange[0]) : null;
      const endDate = dateRange[1] ? formatDateForAPI(dateRange[1]) : null;

      if (!startDate || !endDate) {
        setError('Please select a valid date range');
        return;
      }

      // Fetch data - RTC के लिए section change
      const result = await fetchCleaningActivityDataTurmeric({
        startDate: startDate,
        endDate: endDate,
        section: "rtc"  // Changed from "turmeric" to "rtc"
      });

      let data;
      if (result?.body?.success?.data) {
        data = result.body.success.data;
      } else if (result?.data) {
        data = result.data;
      } else if (result?.body?.data) {
        data = result.body.data;
      } else {
        setError("No data received from server");
        return;
      }

      if (!data) {
        setError("No data received from server");
        return;
      }

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Helper function for sheet names
      const getSheetName = (key) => {
        switch (key) {
          case 'primix1': return 'Premixer1';
          case 'primix2': return 'Premixer2';
          case 'final_mixture1': return 'Final Mixer1';
          case 'final_mixture2': return 'Final Mixer2';
          default: return key;
        }
      };

      // Process all mixer types - Updated keys for RTC
      ['primix1', 'primix2', 'final_mixture1', 'final_mixture2'].forEach(mixerKey => {
        let mixerRecords = data[mixerKey];

        if (!mixerRecords || mixerRecords.length === 0) {
          console.warn(`No records found for ${mixerKey}`);
          const emptySheet = XLSX.utils.json_to_sheet([{
            'Sr. No.': 'No data available',
            Date: 'No data available',
            Time: 'No data available',
            Image: 'No data available'
          }]);
          XLSX.utils.book_append_sheet(
            workbook,
            emptySheet,
            getSheetName(mixerKey)
          );
          return;
        }

        const formattedRecords = mixerRecords.map((record, index) => ({
          'Sr. No.': index + 1,
          Date: record.Date,        // Direct API field
          Time: record.Time,        // Direct API field  
          Image: record.Image,      // Direct API field
          Mixer: getSheetName(mixerKey)  // Changed from "Hopper" to "Mixer"
        }));

        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(formattedRecords);

        // Set column widths
        const colWidths = [
          { wch: 8 },  // Sr. No.
          { wch: 12 }, // Date
          { wch: 15 }, // Time
          { wch: 60 }, // Image URL
          { wch: 15 }  // Mixer (increased width for longer names)
        ];
        worksheet['!cols'] = colWidths;

        // Add sheet to workbook
        const sheetName = getSheetName(mixerKey);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      // Generate and save file - Updated filename
      const wbout = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const filename = `RTC_Cleaning_Activity_${startDate}_to_${endDate}.xlsx`;
      saveAs(blob, filename);

      console.log('Excel file downloaded successfully');

    } catch (error) {
      console.error('Error downloading Excel file:', error);
      setError('Failed to download Excel file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadUnsafeEventsExcel = () => {
    try {
      const startDate = dateRange[0] ? formatDateForAPI(dateRange[0]) : null;
      const endDate = dateRange[1] ? formatDateForAPI(dateRange[1]) : null;

      if (!startDate || !endDate) {
        setError('Please select a valid date range');
        return;
      }

      const workbook = XLSX.utils.book_new();

      const components = [
        { key: 'primix1', sheetName: 'Premixer1' },
        { key: 'primix2', sheetName: 'Premixer2' },
        { key: 'finalmixer1', sheetName: 'Final Mixer1' },
        { key: 'finalmixer2', sheetName: 'Final Mixer2' }
      ];

      components.forEach(({ key, sheetName }) => {
        const events = unsafeEvents[key] || [];

        if (!events || events.length === 0) {
          const emptySheet = XLSX.utils.json_to_sheet([{
            'Sr. No.': 'No data available',
            Date: '',
            Time: '',
            Status: '',
            Image: ''
          }]);
          XLSX.utils.book_append_sheet(workbook, emptySheet, sheetName);
          return;
        }

        const formattedRecords = events.map((event, index) => {
          const date = new Date(event.timestamp);
          const dateStr = !isNaN(date)
            ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
            : '';
          const timeStr = !isNaN(date)
            ? date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
            : '';

          return {
            'Sr. No.': index + 1,
            Date: dateStr,
            Time: timeStr,
            Status: event.testing === true ? 'Present' : 'Removed',
            Image: event.file_name || ''
          };
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedRecords);
        worksheet['!cols'] = [
          { wch: 8 },   // Sr. No.
          { wch: 15 },  // Date
          { wch: 12 },  // Time
          { wch: 12 },  // Status
          { wch: 60 }   // Image URL
        ];

        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      });

      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const filename = `RTC_Unsafe_Events_${startDate}_to_${endDate}.xlsx`;
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error downloading unsafe events Excel:', error);
      setError('Failed to download unsafe events Excel file. Please try again.');
    }
  };

  const formatIfTimestamp = (
    val,
    options = { showDate: true, showTime: true }
  ) => {
    if (
      typeof val === "string" &&
      (val.includes("T") || val.match(/\d{2}:\d{2}/))
    ) {
      const date = new Date(val);
      if (!isNaN(date)) {
        const formattedDate = options.showDate
          ? date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          : "";

        const formattedTime = options.showTime
          ? date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
          : "";

        if (options.showDate && options.showTime) {
          return `${formattedDate}, ${formattedTime}`;
        }
        return formattedDate || formattedTime;
      }
    }
    return val;
  };


  // Handle page change for PrimeReact paginator
  const onPageChange = (event) => {
    const page = event.page + 1; // PrimeReact is 0-based
    const rowsPerPage = event.rows;

    // Fetch data with the new pagination values
    fetchTableData(page, rowsPerPage, dateRange[0], dateRange[1], getEquipmentParam());
  };

  // Apply shift filter to data
  const applyShiftFilter = (key) => {
    // Store the selected shift
    const selectedShift = key === "null" ? null : key;
    setShiftFilter(selectedShift);
    fetchTableData(
      1,
      pagination.rowsPerPage,
      dateRange[0],
      dateRange[1],
      getEquipmentParam()
    );
  };

  function MetricCard({ title, value, icon, isTimestamp = false }) {
    return (
      <div className="metric-card">
        <div className="p-3">
          <div className="metric-title">{title}</div>
          <div className="d-flex align-items-center justify-content-between">
            <div className={`metric-value ${isTimestamp ? "timestamp" : ""}`}>
              {value || "--"}
            </div>
            <div>{icon}</div>
          </div>
        </div>
      </div>
    );
  }

  function StatusCard({ title, status, lidStatus, icon, isEyeIcon }) {
    const [, setShowStatusModal] = useState(false);

    // Determine what to display based on lid presence
    const getDisplayStatus = () => {
      if (lidStatus === "present") {
        return {
          text: "Lid Closed",
          isGood: true,
          color: "lid-close"
        };
      } else if (lidStatus === "absent") {
        // Show safety mesh status when lid is absent
        return {
          text: status ? "Present" : "Removed",
          isGood: status,
          color: status ? "success" : "danger"
        };
      } else {
        // Fallback to safety mesh status
        return {
          text: status ? "Present" : "Removed",
          isGood: status,
          color: status ? "success" : "danger"
        };
      }
    };

    const displayStatus = getDisplayStatus();

    return (
      <div className="status-card">
        {/* Gradient corner */}
        <div className={`status-gradient ${displayStatus.color}`}>
        </div>

        <div
          className="p-3"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <div className="d-flex align-items-center">
            <div
              className={`status-icon-wrapper ${displayStatus.color}`}
            >
              {icon}
            </div>
            <div>
              {/* <div className="status-title">{title}</div> */}
              {!lidStatus && (
                <div className="status-title">{title}</div>
              )}
              <div className={`status-value ${displayStatus.color}`}>
                {displayStatus.text}
              </div>
            </div>
          </div>
          {isEyeIcon && (
            <div style={{ width: "40px", height: "40px", marginRight: "2rem" }}>
              <FiEye
                onClick={() => setShowStatusModal(true)}
                style={{ cursor: "pointer" }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleCleaningHistoryClick = (component, type) => {
    setCurrentCleaningComponent(component);
    setCurrentCleaningType(type);
    setShowCleaningHistoryModal(true);
  };


  // Modal component
  const ImageModal = () =>
    showModal && (
      <div
        className="modal show"
        style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{modalTitle}</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowModal(false)}
              ></button>
            </div>
            <div className="modal-body text-center">
              <img
                src={selectedImage}
                alt="Details"
                style={{ maxWidth: "100%" }}
              />
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div>
      <div>
        <InfoHeader />
      </div>
      <div
        className="col-lg-12"
        style={{
          maxHeight: "90vh",
          overflowY: "scroll",
        }}
      >
        <div className="card border dashboard-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h2 className="fs-5 fw-semibold mb-0">RTC</h2>
            <div className="live-icon position-absolute top-0 end-0 m-2">
              <span className="text-danger text-red-900 text-dark-red text-wight-900 live-badge">
                Live
              </span>
              &nbsp; <span className="dot bg-danger"></span>
            </div>
          </div>
          <div className="card-body">
            <div style={dateFilterContainerStyle}>
              <div className="d-flex align-items-center">
                <FiCalendar className="me-2" size={18} />
                <span className="fw-semibold">Filter by Date Range:</span>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <Calendar
                  value={dateRange}
                  onChange={(e) => {
                    const newDateRange = e.value;
                    setDateRange(newDateRange);

                    // Auto apply filter when complete date range is selected
                    if (newDateRange &&
                      Array.isArray(newDateRange) &&
                      newDateRange.length === 2 &&
                      newDateRange[0] &&
                      newDateRange[1]) {

                      // Small delay to ensure state updates properly
                      setTimeout(() => {
                        fetchCardData(newDateRange[0], newDateRange[1]);
                        fetchTableData(1, pagination.rowsPerPage, newDateRange[0], newDateRange[1], getEquipmentParam());
                        setShiftFilter(null); // Reset shift filter when auto-applying date filter
                      }, 150);
                    }
                  }}
                  selectionMode="range"
                  readOnlyInput
                  showIcon
                  placeholder="Select Date Range"
                  maxDate={new Date()}
                  className="p-calendar-range"
                  dateFormat="dd/mm/yy"
                  hideOnRangeSelection={true}
                />
                <Button
                  variant="primary"
                  onClick={applyDateFilter}
                  disabled={loading}
                  style={{ backgroundColor: "#2196f3", borderRadius: "0px" }}
                >
                  <i className="pi pi-filter me-2"></i>
                  Apply Filter
                </Button>
                <Button
                  variant="outline-secondary"
                  icon="pi pi-times"
                  onClick={clearFilters}
                  disabled={loading}
                  style={{ backgroundColor: "#2196f3", borderRadius: "0px", color: 'white' }}
                />
                <Button
                  label={loading ? "Downloading..." : "Cleaning Activity"}
                  icon={loading ? "pi pi-spin pi-spinner" : "pi pi-download"}
                  onClick={handleDownloadExcel}
                  disabled={loading || !dateRange[0] || !dateRange[1]}
                  className="p-button-md p-button-primery"
                  style={{
                    marginLeft: '10px',
                    minWidth: '200px'
                  }}
                  tooltip={
                    !dateRange[0] || !dateRange[1]
                      ? "Please select a date range first"
                      : "Download cleaning activity data as Excel file for premixer1, premixer2, final mixer1 and final mixer2"
                  }
                  tooltipOptions={{ position: 'top' }}
                />
                <Button
                  label="Unsafe Events"
                  icon="pi pi-download"
                  onClick={handleDownloadUnsafeEventsExcel}
                  disabled={loading || !dateRange[0] || !dateRange[1]}
                  className="p-button-md p-button-primery"
                  style={{
                    marginLeft: '10px',
                    minWidth: '200px'
                  }}
                  tooltip={
                    !dateRange[0] || !dateRange[1]
                      ? "Please select a date range first"
                      : "Download unsafe events data as Excel file for premixer1, premixer2, final mixer1 and final mixer2"
                  }
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
              <div style={tableHeadingStyle}>
              </div>
            </div>
            {loading && (
              <div className="text-center my-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading data...</p>
              </div>
            )}
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <div className="col-3">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <p>Premixer 1</p>
                  <div>
                    <Button
                      style={{ backgroundColor: "#e03730", border: "none", fontSize: "12px" }}
                      onClick={() => {
                        setCurrentComponent("primix1");
                        setShowUnsafeModal(true);
                        setActiveIndex(0);
                        setCurrentRtcComponent("primix1");
                      }}
                    >
                      Unsafe Events - {cardData.primix1?.unsafe_event?.length || 0}
                    </Button>
                    <FaHistory
                      className="history-icon"
                      style={{
                        cursor: "pointer",
                        color: "#6c757d",
                        fontSize: "1.5rem",
                        marginLeft: "1rem",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCleaningHistoryClick("premixer1", "rtc");
                      }}
                      title="View magnetic grill cleaning history"
                    />
                  </div>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-around" }}
                >
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Magnetic rod/plate cleaning time (CCP)"
                      value={cardData?.primix1?.last_magnetic_plate_cleaning_time}
                      icon={<FaMagnet className="metric-icon purple" />}
                      isTimestamp
                    />
                  </div>
                  <div style={{ width: "47%" }}>
                    <StatusCard
                      title="Safety mesh"
                      status={cardData.primix1.safety_mesh_lifted_status === "present"}
                      lidStatus={cardData.primix1.lid_presence}
                      icon={<SiTraefikmesh className="status-icon" />}
                    />
                  </div>
                </div>
              </div>
              <div className="col-3">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <p>Premixer 2</p>
                  <div>
                    <Button
                      style={{ backgroundColor: "#e03730", border: "none", fontSize: "12px" }}
                      onClick={() => {
                        setCurrentComponent("primix2");
                        setShowUnsafeModal(true);
                        setActiveIndex(0);
                        setCurrentRtcComponent("primix2");
                      }}
                    >
                      Unsafe Events - {cardData.primix2?.unsafe_event?.length || 0}
                    </Button>
                    <FaHistory
                      className="history-icon"
                      style={{
                        cursor: "pointer",
                        color: "#6c757d",
                        fontSize: "1.5rem",
                        marginLeft: "1rem",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCleaningHistoryClick("premixer2", "rtc");
                      }}
                      title="View magnetic grill cleaning history"
                    />
                  </div>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-around" }}
                >
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Magnetic rod/plate cleaning time (CCP)"
                      value={cardData?.primix2?.last_magnetic_plate_cleaning_time}
                      icon={<FaMagnet className="metric-icon purple" />}
                      isTimestamp
                    />
                  </div>
                  <div style={{ width: "47%" }}>
                    <StatusCard
                      title="Safety mesh"
                      status={cardData.primix2.safety_mesh_lifted_status === "present"}
                      lidStatus={cardData.primix2.lid_presence}
                      icon={<SiTraefikmesh className="status-icon" />}
                    />
                  </div>
                </div>
              </div>
              <div className="col-3">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <p>Final Mixture 1</p>
                  <div>
                    <Button
                      style={{ backgroundColor: "#e03730", border: "none", fontSize: "12px" }}
                      onClick={() => {
                        setCurrentComponent("finalmixer1");
                        setShowUnsafeModal(true);
                        setActiveIndex(0);
                        setCurrentRtcComponent("finalmixer1");
                      }}
                    >
                      Unsafe Events- {cardData.finalmixer1?.unsafe_event?.length || 0}
                    </Button>
                    <FaHistory
                      className="history-icon"
                      style={{
                        cursor: "pointer",
                        color: "#6c757d",
                        fontSize: "1.5rem",
                        marginLeft: "1rem",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCleaningHistoryClick("finalmixer1", "rtc");
                      }}
                      title="View magnetic grill cleaning history"
                    />
                  </div>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-around" }}
                >
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Magnetic rod/plate cleaning time (CCP)"
                      value={cardData?.finalmixer1?.last_magnetic_plate_cleaning_time}
                      icon={<FaMagnet className="metric-icon purple" />}
                      isTimestamp
                    />
                  </div>
                  <div style={{ width: "47%" }}>
                    <StatusCard
                      title="Safety mesh"
                      status={cardData?.finalmixer1?.safety_mesh_lifted_status === "present"}
                      lidStatus={cardData?.finalmixer1?.lid_presence}
                      icon={<SiTraefikmesh className="status-icon" />}
                    />
                  </div>
                </div>
              </div>
              <div className="col-3">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <p>Final Mixture 2</p>
                  <div>
                    <Button
                      style={{ backgroundColor: "#e03730", border: "none", fontSize: "12px" }}
                      onClick={() => {
                        setCurrentComponent("finalmixer2");
                        setShowUnsafeModal(true);
                        setActiveIndex(0);
                        setCurrentRtcComponent("finalmixer2");
                      }}
                    >
                      Unsafe Events - {cardData.finalmixer2?.unsafe_event?.length || 0}
                    </Button>
                    <FaHistory
                      className="history-icon"
                      style={{
                        cursor: "pointer",
                        color: "#6c757d",
                        fontSize: "1.5rem",
                        marginLeft: "1rem",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCleaningHistoryClick("finalmixer2", "rtc");
                      }}
                      title="View magnetic grill cleaning history"
                    />
                  </div>
                </div>
                <div
                  style={{ display: "flex", justifyContent: "space-around" }}
                >
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Magnetic rod/plate cleaning time (CCP)"
                      value={cardData?.finalmixer2?.last_magnetic_plate_cleaning_time}
                      icon={<FaMagnet className="metric-icon purple" />}
                      isTimestamp
                    />
                  </div>
                  <div style={{ width: "47%" }}>
                    <StatusCard
                      title="Safety mesh"
                      status={cardData?.finalmixer2?.safety_mesh_lifted_status === "present"}
                      lidStatus={cardData?.finalmixer2?.lid_presence}
                      icon={<SiTraefikmesh className="status-icon" />}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <div className="col-6">
                <p>Grinding 1</p>
                <div
                  style={{ display: "flex", justifyContent: "space-around" }}
                >
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Detected Sack count"
                      value={cardData.grinder1.sack_count.toString()}
                      icon={<FiShoppingBag className="metric-icon blue" />}
                    />
                  </div>
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Detected Bucket count"
                      value={cardData.grinder1.bucket_count.toString()}
                      icon={<FaBucket className="metric-icon blue" />}
                    />
                  </div>
                </div>
              </div>
              <div className="col-6">
                <p>Grinding 2</p>
                <div
                  style={{ display: "flex", justifyContent: "space-around" }}
                >
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Detected Sack count"
                      value={cardData.grinder2.sack_count.toString()}
                      icon={<FiShoppingBag className="metric-icon blue" />}
                    />
                  </div>
                  <div style={{ width: "47%" }}>
                    <MetricCard
                      title="Detected Bucket count"
                      value={cardData.grinder2.bucket_count.toString()}
                      icon={<FaBucket className="metric-icon blue" />}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-4">
              {/* Table */}
              <div className="col-lg-12 col-md-12 mb-2">
                <div style={tableContainerStyle}>
                  {/* View Mode Toggle - Replaced with Dropdown Buttons */}
                  <div style={filterContainerStyle}>
                    <div style={{ width: "33%" }}>
                      <div style={{ display: "flex", gap: "10px" }}>
                        {/* Premixer Dropdown */}
                        <DropdownButton
                          id="dropdown-premixer"
                          title="Premixer"
                          variant={
                            selectedComponent.includes("Premixer")
                              ? "primary"
                              : "outline-primary"
                          }
                        >
                          <Dropdown.Item
                            onClick={() => setSelectedComponent("Premixer 1")}
                          >
                            Premixer 1
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => setSelectedComponent("Premixer 2")}
                          >
                            Premixer 2
                          </Dropdown.Item>
                        </DropdownButton>

                        {/* Grinder Dropdown */}
                        <DropdownButton
                          id="dropdown-grinder"
                          title="Grinder"
                          variant={
                            selectedComponent.includes("Grinder")
                              ? "primary"
                              : "outline-primary"
                          }
                        >
                          <Dropdown.Item
                            onClick={() => setSelectedComponent("Grinder 1")}
                          >
                            Grinder 1
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => setSelectedComponent("Grinder 2")}
                          >
                            Grinder 2
                          </Dropdown.Item>
                        </DropdownButton>

                        {/* Final Mixture Dropdown */}
                        <DropdownButton
                          id="dropdown-final-mixture"
                          title="Final Mixture"
                          variant={
                            selectedComponent.includes("Final Mixture")
                              ? "primary"
                              : "outline-primary"
                          }
                        >
                          <Dropdown.Item
                            onClick={() =>
                              setSelectedComponent("Final Mixture 1")
                            }
                          >
                            Final Mixture 1
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() =>
                              setSelectedComponent("Final Mixture 2")
                            }
                          >
                            Final Mixture 2
                          </Dropdown.Item>
                        </DropdownButton>
                      </div>
                    </div>
                    <div style={tableHeadingStyle}>
                      {selectedComponent} Data
                    </div>
                    <div
                      className="d-flex align-items-center justify-content-end"
                      style={{ width: "33%" }}
                    >
                      <span className="me-2">Filter by Shift:</span>
                      <Dropdown
                        onSelect={(key) => applyShiftFilter(key)}
                      >
                        <Dropdown.Toggle variant="light" id="dropdown-shift">
                          {shiftFilter ? `Shift ${shiftFilter}` : "All Shifts"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item eventKey="null">
                            All Shifts
                          </Dropdown.Item>
                          <Dropdown.Item eventKey="I">Shift I</Dropdown.Item>
                          <Dropdown.Item eventKey="II">Shift II</Dropdown.Item>
                          <Dropdown.Item eventKey="III">Shift III</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                  <DataTable
                    value={data}
                    scrollable
                    scrollHeight="250px"
                    className="p-datatable-sm p-datatable-striped"
                    emptyMessage="No records found for selected date range"
                    responsiveLayout="scroll"
                    loading={loading}
                    paginator={false}
                  >
                    <Column
                      header="Sr. No."
                      style={{ width: "5%" }}
                      bodyStyle={{ textAlign: "right" }}
                      body={(rowData, options) => (
                        <span>
                          {(pagination.currentPage - 1) *
                            pagination.rowsPerPage +
                            options.rowIndex +
                            1}
                        </span>
                      )}
                    />

                    <Column
                      field="timestamp_range"
                      header="Date"
                      style={{ width: viewMode === "grinder" ? "10%" : "25%" }}
                      body={(rowData) => (
                        <span>{extractDateFromTimestampRange(rowData.timestamp_range)}</span>
                      )}
                    />

                    <Column
                      header="Time"
                      style={{ width: viewMode === "grinder" ? "12%" : "20%" }}
                      body={(rowData) => (
                        <span
                          style={{ cursor: "pointer", color: "black" }}
                          onClick={() => {
                            setShowModal(true);
                            setSelectedImage("/Sack_Count.png");
                            setModalTitle(
                              viewMode === "mixer"
                                ? "Sack Emptying Count Details"
                                : "Grinder Details"
                            );
                          }}
                        >
                          {extractTimeFromTimestampRange(rowData.timestamp_range)}
                        </span>
                      )}
                    />

                    <Column
                      field="shift"
                      header="Shift"
                      style={{ width: viewMode === "grinder" ? "7%" : "15%" }}
                      bodyStyle={{ backgroundColor: "rgb(214, 249, 255)", textAlign: "center" }}
                    />

                    {viewMode === "mixer" && (
                      <Column
                        field="safety_mesh_absent_count"
                        header="Safety Mesh Absent"
                        style={{ width: "15%" }}
                        bodyStyle={{
                          backgroundColor: "rgba(222, 234, 252, 0.7)", textAlign: "right"
                        }}
                      />
                    )}

                    {viewMode === "grinder" && (
                      <Column
                        field="sack_count"
                        header="Detected Sack Count"
                        style={{ width: "12%" }}
                        bodyStyle={{
                          backgroundColor: "rgba(233, 236, 239, 0.7)", textAlign: "right"
                        }}
                      />
                    )}

                    {viewMode === "grinder" && (
                      <Column
                        header="Actual Sack Count"
                        style={{ width: "12%" }}
                        body={(rowData) => (
                          <input
                            key={`sack_${rowData.timestamp_range}`}
                            type="text"
                            inputMode="numeric"
                            defaultValue={rowData.actual_sack_count != null && rowData.actual_sack_count !== "" ? rowData.actual_sack_count : ""}
                            onChange={(e) =>
                              handleActualCountChange(
                                rowData.timestamp_range,
                                "actual_sack_count",
                                e.target.value
                              )
                            }
                            placeholder="Enter count"
                            style={{
                              width: "100%",
                              padding: "4px 8px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              textAlign: "right",
                            }}
                          />
                        )}
                      />
                    )}

                    {viewMode === "grinder" && (
                      <Column
                        field="bucket_count"
                        header="Detected Bucket Count"
                        style={{ width: "12%" }}
                        bodyStyle={{
                          backgroundColor: "rgba(222, 234, 252, 0.7)", textAlign: "right"
                        }}
                      />
                    )}

                    {viewMode === "grinder" && (
                      <Column
                        header="Actual Bucket Count"
                        style={{ width: "12%" }}
                        body={(rowData) => (
                          <input
                            key={`bucket_${rowData.timestamp_range}`}
                            type="text"
                            inputMode="numeric"
                            defaultValue={rowData.actual_bucket_count != null && rowData.actual_bucket_count !== "" ? rowData.actual_bucket_count : ""}
                            onChange={(e) =>
                              handleActualCountChange(
                                rowData.timestamp_range,
                                "actual_bucket_count",
                                e.target.value
                              )
                            }
                            placeholder="Enter count"
                            style={{
                              width: "100%",
                              padding: "4px 8px",
                              border: "1px solid #ced4da",
                              borderRadius: "4px",
                              textAlign: "right",
                            }}
                          />
                        )}
                      />
                    )}

                    {viewMode === "grinder" && (
                      <Column
                        header="Action"
                        style={{ width: "7%" }}
                        bodyStyle={{ textAlign: "center" }}
                        body={(rowData) => {
                          const hasExisting = (rowData.actual_sack_count != null && rowData.actual_sack_count !== "") ||
                            (rowData.actual_bucket_count != null && rowData.actual_bucket_count !== "");
                          return (
                            <RowSaveButton
                              key={rowData.timestamp_range}
                              rowKey={rowData.timestamp_range}
                              saveCallbacksRef={saveCallbacksRef}
                              onSave={() => handleSaveActualCount(rowData)}
                              hasExistingValue={hasExisting}
                            />
                          );
                        }}
                      />
                    )}
                  </DataTable>

                  {/* PrimeReact Paginator Component */}
                  <Paginator
                    first={(pagination.currentPage - 1) * pagination.rowsPerPage}
                    rows={pagination.rowsPerPage}
                    totalRecords={pagination.totalRecords}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    onPageChange={onPageChange}
                    className="mt-3"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for displaying image details */}
      <ImageModal />
      <UnsafeEventsModal
        showModal={showUnsafeModal}
        onHide={() => {
          setShowUnsafeModal(false);
          setActiveIndex(0);
        }}
        currentHopper={currentComponent}
        currentRtcComponent={currentComponent}
        unsafeEvents={unsafeEvents}
        rtcUnsafeEvents={rtcUnsafeEvents}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        formatIfTimestamp={formatIfTimestamp}
      />
      {/* <CleaningHistoryModal
        showModal={showCleaningHistoryModal}
        onHide={() => {
          setShowCleaningHistoryModal(false);
          setCurrentCleaningComponent(null);
          setCurrentCleaningType(null);
        }}
        currentComponent={currentCleaningComponent}
        componentType={currentCleaningType}
        formatIfTimestamp={formatIfTimestamp}
        rtcCleaningHistory={cleaningHistoryData}
      /> */}
      <CleaningHistoryModal
        showModal={showCleaningHistoryModal}
        onHide={() => setShowCleaningHistoryModal(false)}
        currentComponent={currentCleaningComponent}
        // turmericCleaningHistory={turmericCleaningHistory}
        rtcCleaningHistory={cleaningHistoryData}
        cleaningType="rtc"
      />
    </div>
  );
};

export default RTC;
